// Display helpers for the parking lot (pages/ParkingLotPage.tsx, components/ParkingNote.tsx).
import { PARKING_LOT_COLUMNS, type ParkingLotColumn, type ParkingLotItem, type ParkingLotLoop, type ParkingLotSize } from '@jabberr/core';
import { longDate, shortDate } from './format.js';

export const BOARD_COLUMNS: ParkingLotColumn[] = ['someday', 'next_up', 'building'];
export const columnLabel = (c: ParkingLotColumn): string => PARKING_LOT_COLUMNS[c];

export const SIZE_LABELS: Record<ParkingLotSize, string> = { small: 'Small', medium: 'Medium', large: 'Large', unknown: 'Size not known' };

/** A new project idea: no project yet, or still a private project. */
export function isNewProjectIdea(item: Pick<ParkingLotItem, 'projectId' | 'projectKind'>): boolean {
  return item.projectId === null || item.projectKind !== 'team';
}

/** The chip on a note: the project's name (Workbench codes are not known to Jabberr), or "New project". */
export function chipLabel(item: Pick<ParkingLotItem, 'projectId' | 'projectKind' | 'projectName'>): string {
  return isNewProjectIdea(item) ? 'New project' : (item.projectName ?? 'Project');
}

const firstName = (name: string | null): string => (name ?? 'Someone').split(/\s+/)[0] ?? 'Someone';

/** "Chris · from a conversation, Oct 3 · Medium" */
export function cardMeta(item: ParkingLotItem): string {
  return [
    firstName(item.createdByName),
    `${item.sourceConversationId ? 'from a conversation, ' : ''}${shortDate(item.createdAt)}`,
    ...(item.size !== 'unknown' ? [SIZE_LABELS[item.size]] : []),
  ].join(' · ');
}

/** "Suggested by Chris · from a conversation with Jabberr on October 3 · rough size: Medium" */
export function noteMeta(item: ParkingLotItem): string {
  return [
    `Suggested by ${firstName(item.createdByName)}`,
    item.sourceConversationId ? `from a conversation with Jabberr on ${longDate(item.createdAt)}` : `added ${longDate(item.createdAt)}`,
    `rough size: ${item.size === 'unknown' ? 'not known yet' : SIZE_LABELS[item.size]}`,
  ].join(' · ');
}

const TINTS = 7;
/** A stable tint for a project (colour by project); new project ideas share tint 0. */
export function projectTint(projectId: string | null): number {
  if (!projectId) return 0;
  let h = 0;
  for (const ch of projectId) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return 1 + (h % (TINTS - 1));
}

/** The person asked Jabberr in a comment (the server decides the same way). */
export const namesJabberr = (body: string): boolean => /(^|[^\w@])@jabberr?\b/i.test(body);

const WRAP_UP = /\b(that'?s (enough|all)( for (now|today))?|that'?s it for (now|today)|enough for (now|today)|done for (now|today)|(let'?s|we can|i'?ll) (wrap|stop|leave it|park it)|wrap (it |this |things )?up|i'?m done|we'?re done|i think we'?re good|park (it|this))\b/i;

/**
 * The end-of-conversation card shows after Jabberr's reply when the person has just said they are wrapping up: the
 * latest turns are their message with one of the phrases above, then a completed reply. Kept simple on purpose; the
 * same choice is always in the Write... menu.
 */
export function wantsToWrapUp(turns: Array<{ kind: string; status: string; text: string; purpose?: string | null }>): string | null {
  const talk = turns.filter((t) => t.kind === 'user' || t.kind === 'assistant');
  const reply = talk.at(-1);
  const asked = talk.at(-2);
  if (!reply || !asked || reply.kind !== 'assistant' || reply.status !== 'completed' || asked.kind !== 'user' || asked.purpose) return null;
  return WRAP_UP.test(asked.text) ? asked.text : null;
}

/** The note's one line about Loop (Parking lot Part 2). */
export function loopLine(loop: ParkingLotLoop): string {
  const what = loop.mode === 'new' ? 'as a new project' : `to extend ${loop.targetProjectName ?? 'a project'}`;
  const by = loop.decidedByName ? ` by ${firstName(loop.decidedByName)}` : '';
  switch (loop.status) {
    case 'waiting':
      return `Waiting in Loop for John or Adam ${what}, sent by ${firstName(loop.sentByName)} ${shortDate(loop.sentAt)}.`;
    case 'approved':
      return loop.mode === 'new'
        ? `Approved in Loop${by}: ${loop.yardProjectName ?? 'the new project'} is being planned.`
        : `Approved in Loop${by}: planned on ${loop.targetProjectName ?? loop.yardProjectName ?? 'the project'}.`;
    case 'sent_back':
      return `Sent back from Loop${by}: ${loop.reason ?? 'see the thread'}`;
    case 'expired':
      return 'The request expired in Loop without a decision. Send it again when it is ready.';
    default:
      return `Approved in Loop${by}, but Anchor Yard could not carry it out${loop.reason ? `: ${loop.reason}` : ''}.`;
  }
}
